import type { GeneralSettings } from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { isSameValue } from '@/lib/is-same-value.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { Switch } from '@/components/ui/switch.js';
import { ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { fetchGeneralSettings, fetchTestMode, updateGeneralSettings, updateTestMode } from './api-general.js';
import { MarteletSuspensionSection } from './martelet-suspension-section.js';
import { settingsKeys } from './query-keys.js';
import { SettingsSectionSkeleton } from './settings-parts.js';

/**
 * Chaque seuil est accompagné d'une phrase qui dit **ce qui change** quand on
 * le modifie : ce sont des réglages qu'on touche une fois par an, sans se
 * souvenir de ce qu'ils font.
 */
const FIELDS: { name: keyof GeneralSettings; label: string; hint: string }[] = [
  {
    name: 'defaultOffsetMinutes',
    label: 'Délai avant préparation d’une commande (minutes)',
    hint: 'Temps d’attente global après le paiement avant de préparer une commande, pour laisser passer une annulation ou une correction d’adresse.',
  },
  {
    name: 'csvRetentionDays',
    label: 'Conservation du contenu des CSV (jours)',
    hint: 'Passé ce délai, les fichiers restent listés dans Fichiers mais leur contenu n’est plus consultable ni renvoyable.',
  },
  {
    name: 'eventsRetentionDays',
    label: 'Conservation du journal (jours)',
    hint: 'Les événements plus anciens sont supprimés automatiquement.',
  },
  {
    name: 'pollIntervalMinutes',
    label: 'Filet de sécurité : relecture des commandes Shopify (minutes)',
    hint: 'Fréquence à laquelle les commandes sont relues directement chez Shopify, au cas où un webhook se serait perdu.',
  },
  {
    name: 'noReturnFilesBusinessDays',
    label: 'Alerter si Martelet ne dépose plus de retour depuis (jours ouvrés)',
    hint: 'Compté en jours ouvrés : un week-end sans retour n’est pas une anomalie.',
  },
];

type FormValues = Record<keyof GeneralSettings, string>;

function toFormValues(data: GeneralSettings): FormValues {
  return {
    defaultOffsetMinutes: String(data.defaultOffsetMinutes),
    csvRetentionDays: String(data.csvRetentionDays),
    eventsRetentionDays: String(data.eventsRetentionDays),
    pollIntervalMinutes: String(data.pollIntervalMinutes),
    webhookSilenceHours: String(data.webhookSilenceHours),
    noReturnFilesBusinessDays: String(data.noReturnFilesBusinessDays),
  };
}

/**
 * Onglet **Avancé** (`02` §12.5), ex « Général » : les six seuils qui règlent
 * le rythme de la plateforme. Renommé parce qu'« Avancé » dit ce qu'il faut
 * savoir avant d'y toucher — « Général » invitait à commencer par là.
 *
 * Une seule colonne (et non une grille à deux colonnes) : libellés et aides
 * sont longs, deux colonnes les feraient passer à la ligne de façon inégale et
 * désaligneraient les champs.
 */
export function AvanceTab(): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: settingsKeys.general, queryFn: fetchGeneralSettings });
  const testModeQuery = useQuery({ queryKey: settingsKeys.testMode, queryFn: fetchTestMode });
  const testModeMutation = useMutation({
    mutationFn: updateTestMode,
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.testMode, data);
      toast.success(data.enabled ? 'Mode test activé.' : 'Mode test désactivé. Les prochains traitements seront réels.');
    },
    onError: (error: unknown) => toast.fromError(error),
  });
  const [values, setValues] = useState<FormValues | null>(null);

  useEffect(() => {
    if (query.data) {
      setValues(toFormValues(query.data));
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: updateGeneralSettings,
    onSuccess: (data) => {
      toast.success('Réglages généraux mis à jour.');
      queryClient.setQueryData(settingsKeys.general, data);
      setValues(toFormValues(data));
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  if (query.isPending || !values) {
    return <SettingsSectionSkeleton fields={6} />;
  }
  if (query.isError) {
    return (
      <ErrorState
        message={
          query.error instanceof ApiError
            ? query.error.message
            : 'Une erreur technique est survenue.'
        }
        detail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const isDirty = !isSameValue(values, toFormValues(query.data));

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!values) {
      return;
    }
    const parsedEntries = FIELDS.map((field) => [field.name, Number(values[field.name])] as const);
    const invalid = parsedEntries.find(([, value]) => !Number.isFinite(value) || value < 1);
    if (invalid) {
      toast.error('Toutes les valeurs doivent être des nombres entiers positifs.');
      return;
    }
    const body = { ...query.data, ...Object.fromEntries(parsedEntries) } as GeneralSettings;
    mutation.mutate(body);
  }

  return (
    <div className="flex flex-col gap-6">
    <PageSection title="Mode test global" description="Les lectures et calculs continuent ; aucun fichier n’est déposé et aucune mise à jour Shopify n’est faite. Les alertes restent envoyées, marquées « Mode test ».">
      <label className="flex items-center gap-3 text-sm font-medium">
        <Switch checked={testModeQuery.data?.enabled ?? false} disabled={testModeQuery.isPending || testModeMutation.isPending}
          onCheckedChange={(enabled) => testModeMutation.mutate(enabled)} />
        {testModeQuery.data?.enabled ? 'Mode test actif' : 'Mode test inactif'}
      </label>
      <p className="mt-2 text-xs text-muted-foreground">Les traitements simulés ne sont pas rejoués automatiquement à la désactivation.</p>
    </PageSection>
    <MarteletSuspensionSection />
    <form onSubmit={handleSubmit}>
      <PageSection
        title="Réglages avancés"
        description="Réglages qui n’ont normalement pas besoin d’être modifiés."
        footer={
          <SaveButton isLoading={mutation.isPending} isDirty={isDirty} />
        }
      >
        <div className="flex flex-col gap-5">
          {FIELDS.map((field) => (
            <Field
              key={field.name}
              label={field.label}
              htmlFor={field.name}
              hint={field.hint}
              className="max-w-xl"
            >
              <Input
                id={field.name}
                type="number"
                min={1}
                step={1}
                className="sm:w-40"
                value={values[field.name]}
                onChange={(event) => {
                  setValues((prev) =>
                    prev ? { ...prev, [field.name]: event.target.value } : prev,
                  );
                }}
              />
            </Field>
          ))}
        </div>
      </PageSection>
    </form>
    </div>
  );
}
